import { defineStore } from 'pinia'
import { api } from '../services/api.js'
import { isNative, getMobileRedirectUri, getOrCreateDeviceId } from '../services/platform.js'

export const useAppStore = defineStore('auth', {
  state: () => ({
    accessToken: localStorage.getItem('access_token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    loadingAuth: false,
    authError: null,

    // serverConfig: risposta di /api/v2/config (auth, server, user)
    serverConfig: (() => {
      try {
        const raw = sessionStorage.getItem('serverConfig') || localStorage.getItem('app_server_config')
        return JSON.parse(raw || 'null')
      } catch {
        return null
      }
    })(),

    // true quando il server non è raggiungibile e si usa la cache
    isOffline: false,
  }),

  getters: {
    isAuthenticated(state) {
      return !!(state.user && state.user.uid && state.user.uid !== 0)
    },

    isAdmin(state) {
      return (state.user?.roles || []).includes('admin')
    },

    isRegAdmin(state) {
      return (state.user?.roles || []).includes('reg_admin')
    },

    isRtOperatore(state) {
      return (state.user?.roles || []).includes('rt_operatore')
    },

    getUserName(state) {
      if (!state.user) return ''
      const su = state.user.superuser
      if (su) return `${su.realname || ''} ${su.realsurname || ''}`.trim()
      return state.user.name || state.user.email || ''
    },

    hasServerConfig(state) {
      return state.serverConfig !== null
    },

    getAuthConfig(state) {
      return state.serverConfig?.auth?.oauth2 || null
    },

    isLoading(state) {
      return state.loadingAuth
    },

    getAuthError(state) {
      return state.user?.auth_error || null
    },
  },

  actions: {
    async fetchServerConfig() {
      this.loadingAuth = true
      this.authError = null

      try {
        const guid = getOrCreateDeviceId()
        const res = await api.get('/api/v2/config', guid ? { params: { guid_device: guid } } : undefined)
        if (!res.ok) throw new Error(res.message || 'Errore caricamento config')

        const config = res.data
        this.serverConfig = config
        this.isOffline = false

        sessionStorage.setItem('serverConfig', JSON.stringify(config))
        localStorage.setItem('app_server_config', JSON.stringify(config))

        if (config.user) {
          this.user = config.user
          localStorage.setItem('user', JSON.stringify(config.user))
        }
        return config
      } catch (error) {
        // Fallback da cache locale
        const cached = localStorage.getItem('app_server_config')
        if (cached) {
          try {
            this.serverConfig = JSON.parse(cached)
            this.isOffline = true
            return this.serverConfig
          } catch {
            localStorage.removeItem('app_server_config')
          }
        }
        this.authError = error.message
        throw error
      } finally {
        this.loadingAuth = false
      }
    },

    login() {
      const oauth = this.serverConfig?.auth?.oauth2
      if (!oauth) return

      let redirect_uri = oauth.redirect_uri
      if (isNative()) redirect_uri = getMobileRedirectUri()

      const scopes = oauth.scopes ? '&scope=' + oauth.scopes.join(' ') : ''
      const url = (oauth.authorization_endpoint || oauth.auth_url)
        + '?response_type=code'
        + scopes
        + '&redirect_uri=' + redirect_uri
        + '&client_id=' + oauth.client_id

      try {
        localStorage.setItem('post_login_redirect', window.location.href)
      } catch {}

      window.location = url
    },

    async resolveCodeOAuth2() {
      // Con hash routing l'URL è: http://host/#/auth/callback?code=xxx
      const hashParts = window.location.hash.split('?')
      const queryString = hashParts.length > 1 ? hashParts[1] : window.location.search.substring(1)
      const params = new URLSearchParams(queryString)
      const code = params.get('code')

      if (!code) return

      this.loadingAuth = true
      this.authError = null

      const authConfig = this.getAuthConfig
      if (!authConfig) {
        this.authError = 'Configurazione OAuth2 non disponibile'
        this.loadingAuth = false
        return
      }

      try {
        const guid = getOrCreateDeviceId()
        const res = await api.get('/api/v2/config', {
          params: {
            code,
            oauth_to_be_use: 'oauth2',
            redirect_uri: isNative() ? getMobileRedirectUri() : authConfig.redirect_uri,
            ...(guid ? { guid_device: guid } : {}),
          },
        })

        if (!res.ok) throw new Error(res.message || 'Errore ottenimento token')

        const data = res.data
        if (!data?.user?.access_token) throw new Error('Token non ricevuto')

        this.setTokens({
          access_token: data.user.access_token,
          refresh_token: data.user.refresh_token,
          expires_in: data.expires_in || 3600,
          user: data.user,
        })

        this.serverConfig = data
        sessionStorage.setItem('serverConfig', JSON.stringify(data))
        localStorage.setItem('app_server_config', JSON.stringify(data))

      } catch (error) {
        this.authError = error.message
      } finally {
        // Pulisce i query param dall'hash
        const cleanHash = window.location.hash.split('?')[0]
        window.history.replaceState({}, document.title, window.location.pathname + cleanHash)
        this.loadingAuth = false
      }
    },

    setTokens({ access_token, refresh_token, expires_in, user }) {
      const expiresAt = Date.now() + (expires_in || 3600) * 1000
      this.accessToken = access_token
      this.refreshToken = refresh_token
      this.user = user || null
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token || '')
      localStorage.setItem('expires_at', expiresAt)
      localStorage.setItem('user', JSON.stringify(user || {}))
    },

    async logout() {
      this.loadingAuth = true

      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('expires_at')
      localStorage.removeItem('user')
      localStorage.removeItem('selected_profile')

      this.accessToken = null
      this.refreshToken = null
      this.user = null

      try {
        const guid = getOrCreateDeviceId()
        await api.get('/api/v2/config', { params: { logout: 'true', ...(guid ? { guid_device: guid } : {}) } })
      } catch {}

      window.location.href = '/'
    },

    hasRole(role) {
      return (this.user?.roles || []).includes(role)
    },

    clearAuthError() {
      this.authError = null
    },
  },
})
